import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RealWeddings = () => {
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeddings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/weddings?featured=true`);
        setWeddings(response.data.slice(0, 4)); // Get first 4 featured weddings
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeddings();
  }, []);

  if (loading) {
    return (
      <section className="py-24" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p style={{ color: '#757B7F' }}>Loading weddings...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24" style={{ backgroundColor: '#FAF9F7' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-4">
          <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: '#757B7F' }}>
            Real love. Real celebrations.
          </p>
          <h2 
            className="text-4xl md:text-5xl mb-4 tracking-tight"
            style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}
          >
            Real-Life Weddings
          </h2>
          <p className="text-base max-w-3xl mx-auto mb-12" style={{ color: '#757B7F' }}>
            Discover beautifully curated real weddings, heartfelt love stories, and the meaningful details that made each day truly unforgettable.
          </p>
        </div>

        {/* Weddings Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {weddings.map((wedding) => (
            <Link key={wedding.id} to={`/wedding-stories/${wedding.slug || wedding.id}`}>
              <Card className="group overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-white h-full">
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={wedding.image} 
                    alt={`${wedding.couple}, real wedding at ${wedding.venue || 'Malta'}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Read Story Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="bg-white/90 px-4 py-2 rounded text-sm tracking-wider flex items-center gap-2" style={{ color: '#2C2C2C' }}>
                      READ STORY <ArrowRight size={14} />
                    </span>
                  </div>
                </div>

                {/* Content */}
                <CardContent className="p-5">
                  <h3 
                    className="text-xl mb-2 transition-colors leading-tight"
                    style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#DE92A9'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#2C2C2C'}
                  >
                    {wedding.couple}
                  </h3>
                  
                  <div className="flex items-center gap-3 text-xs mb-2" style={{ color: '#757B7F' }}>
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      <span>{wedding.date}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-1 text-xs mb-3" style={{ color: '#757B7F' }}>
                    <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-1">{wedding.venue}</span>
                  </div>
                  
                  <p className="text-sm leading-relaxed line-clamp-3" style={{ color: '#757B7F' }}>
                    {wedding.description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center mt-10">
          <Link 
            to="/wedding-stories"
            className="inline-flex items-center gap-2 text-sm tracking-wider hover:opacity-70 transition-opacity group"
            style={{ color: '#DE92A9' }}
          >
            VIEW ALL WEDDING STORIES
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RealWeddings;
